import { useState, useCallback, useRef } from "react";
import type { EditorView } from "prosemirror-view";
import type { NLSDocument } from "../documents/types.ts";
import type { ViewspecState } from "../viewspecs/types.ts";
import {
  findCommand,
  getAllCommands,
  searchCommands,
} from "../editor/commands/index.ts";
import type {
  NLSCommand,
  CommandContext,
  CommandArg,
} from "../editor/commands/index.ts";
import { parseViewspecCodes } from "../viewspecs/codes.ts";

export type CommandBarState =
  | { mode: "idle" }
  | { mode: "command"; input: string }
  | {
      mode: "collecting-args";
      command: NLSCommand;
      collectedArgs: Record<string, string>;
      currentArgIndex: number;
    };

interface UseCommandsOptions {
  editorViewRef: React.RefObject<EditorView | null>;
  currentDocument: NLSDocument | null;
  navigate: (path: string) => void;
  setViewspecs: (vs: Partial<ViewspecState>) => void;
  focusEditor: () => void;
}

export function useCommands(options: UseCommandsOptions) {
  const [barState, setBarState] = useState<CommandBarState>({ mode: "idle" });
  const [isActive, setIsActive] = useState(false);

  const makeContext = useCallback((): CommandContext => {
    return {
      editorView: options.editorViewRef.current,
      currentDocument: options.currentDocument,
      navigate: options.navigate,
      setViewspecs: options.setViewspecs,
      focusEditor: options.focusEditor,
    };
  }, [options]);

  const focusCommandBar = useCallback(() => {
    setIsActive(true);
    setBarState({ mode: "command", input: "" });
  }, []);

  const blurCommandBar = useCallback(() => {
    setIsActive(false);
    setBarState({ mode: "idle" });
    options.focusEditor();
  }, [options.focusEditor]);

  const executeCommand = useCallback(
    (nameOrAlias: string, args?: Record<string, string>) => {
      const cmd = findCommand(nameOrAlias);
      if (!cmd) return;

      if (args || cmd.args.length === 0) {
        // Execute immediately
        cmd.execute(makeContext(), args ?? {});
        blurCommandBar();
      } else {
        // Start collecting args
        setBarState({
          mode: "collecting-args",
          command: cmd,
          collectedArgs: {},
          currentArgIndex: 0,
        });
        setIsActive(true);
      }
    },
    [makeContext, blurCommandBar]
  );

  const submitInput = useCallback(
    (input: string) => {
      if (barState.mode === "command") {
        // Check for viewspec code shortcut (: prefix)
        if (input.startsWith(":")) {
          const codes = input.slice(1);
          options.setViewspecs(parseViewspecCodes(codes));
          blurCommandBar();
          return;
        }

        // Try to find and execute command
        const cmd = findCommand(input);
        if (cmd) {
          if (cmd.args.length === 0) {
            cmd.execute(makeContext(), {});
            blurCommandBar();
          } else {
            // Start arg collection
            const requiredArgs = cmd.args.filter((a) => !a.optional);
            setBarState({
              mode: "collecting-args",
              command: cmd,
              collectedArgs: {},
              currentArgIndex: 0,
            });
          }
        }
      } else if (barState.mode === "collecting-args") {
        const { command, collectedArgs, currentArgIndex } = barState;
        const currentArg = command.args[currentArgIndex];

        if (currentArg) {
          const newCollected = {
            ...collectedArgs,
            [currentArg.name]: input,
          };
          const nextIndex = currentArgIndex + 1;

          // Skip optional args if input is empty
          if (
            nextIndex >= command.args.length ||
            (input === "" && currentArg.optional)
          ) {
            // All args collected — execute
            command.execute(makeContext(), newCollected);
            blurCommandBar();
          } else {
            setBarState({
              mode: "collecting-args",
              command,
              collectedArgs: newCollected,
              currentArgIndex: nextIndex,
            });
          }
        }
      }
    },
    [barState, makeContext, blurCommandBar, options.setViewspecs]
  );

  const currentPrompt = (() => {
    if (barState.mode === "collecting-args") {
      const arg = barState.command.args[barState.currentArgIndex];
      return arg?.prompt ?? ">";
    }
    return ">";
  })();

  return {
    barState,
    isActive,
    currentPrompt,
    focusCommandBar,
    blurCommandBar,
    submitInput,
    executeCommand,
    searchCommands,
    getAllCommands,
  };
}
